import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ClipboardList, X } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { createTask, updateTask } from "../../api/tasks";
import { useTaskTranslate } from "./useTaskTranslate";
import { dictationSupported, useDictation } from "./useDictation";
import { TaskBilingualField } from "./TaskBilingualField";
import { TaskFormMeta } from "./TaskFormMeta";

const todayISO = () => new Date().toISOString().slice(0, 10);
const INIT = {
  title_ar: "", title_en: "", description_ar: "", description_en: "", branch_id: "", assignee_id: "",
  start_date: todayISO(), due_date: todayISO(), due_time: "", priority: "medium",
  requires_approval: true, requires_attachment: false,
};

export const TaskForm = ({ task, branches, employees, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(task ? { ...INIT, ...task } : INIT);
  const { translating, autofill } = useTaskTranslate(setForm);
  const dict = useDictation((key, text) => setForm((f) => ({ ...f, [key]: (f[key] ? f[key] + " " : "") + text })));
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const onField = (key, val) => { setForm((f) => ({ ...f, [key]: val })); autofill(key, val); };
  const dictate = dictationSupported ? (key, lng) => dict.start(key, lng) : null;

  const submit = async () => {
    if (!(form.title_ar || form.title_en)) return toast.error(t("tasks.form.titleRequired"));
    if (!form.assignee_id) return toast.error(t("tasks.form.assigneeRequired"));
    if (!form.due_date) return toast.error(t("tasks.form.dueRequired"));
    try {
      if (task) await updateTask(task.id, form); else await createTask(form);
      toast.success(t(task ? "tasks.form.updated" : "tasks.form.created"));
      onSaved();
      onClose();
    } catch {
      toast.error(t("tasks.form.saveFailed"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/80 p-4"
         data-testid="task-form-modal" onClick={onClose}>
      <NeuCard className="grid max-h-[92vh] w-full max-w-2xl gap-4 overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="task-form" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-primary shadow-neu-in"><ClipboardList className="h-5 w-5" /></span>
            <div className="grid">
              <h3 className="text-lg font-black leading-tight sm:text-base">{t(task ? "tasks.form.editTitle" : "tasks.form.newTitle")}</h3>
              <p className="text-sm text-muted-foreground sm:text-xs">{t("tasks.form.hint")}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="task-form-x"
            className="rounded-xl p-1.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <TaskBilingualField base="title" label={t("tasks.fields.title")} values={form} onChange={onField}
            dictate={dictate} listening={dict.listening} translating={translating}
            placeholder={{ ar: t("tasks.fields.titleHintAr"), en: t("tasks.fields.titleHintEn") }} />
          <TaskBilingualField base="description" label={t("tasks.fields.description")} values={form} onChange={onField}
            dictate={dictate} listening={dict.listening} translating={translating} multiline
            placeholder={{ ar: t("tasks.fields.descriptionHintAr"), en: t("tasks.fields.descriptionHintEn") }} />
          <TaskFormMeta form={form} set={set} setForm={setForm} branches={branches} employees={employees} t={t} />
        </div>
        <div className="flex flex-wrap gap-3">
          <NeuButton variant="primary" data-testid="task-submit-btn" onClick={submit}>{t(task ? "common.save" : "tasks.new")}</NeuButton>
          <NeuButton data-testid="task-form-close" onClick={onClose}>{t("common.cancel")}</NeuButton>
        </div>
      </NeuCard>
    </div>
  );
};
